import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, Renderer2, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { SharedService } from '../../shared/services/shared.service';

export interface slideDetails {
  button: string; // Button text and URL in the format "Button Text;URL"
  description: string; // Description of the slide
  id: number; // Unique identifier for the slide
  image: string; // Image URL for the slide
  imagePost?: {
    method: 'POST' | 'post';
    url: string;
    body?: any;
  };
  occurence: number; // Order of the slide in the carousel
  position: string; // Position of the slide in the carousel
  theme: string; // Theme type for the slide (e.g., 'light', 'dark')
  title: string; // Title of the slide
  visibility: string; // Visibility flag for the slide
  safeHTML?: SafeHtml; // Sanitized HTML content for the slide description
  error?: boolean; // Flag to indicate if there was an error loading the image
}
@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.css',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CarouselComponent implements OnChanges, OnDestroy {

  // @ViewChild('carousel', { static: true }) carousel: NgbCarousel;
  currentSlide = 0; // Track the current slide index
  autoplayInterval: any; // Store the interval ID for autoplay
  slides: slideDetails[] = []; // Input property for slides
  @Input() carouselConfig: any = {}; // Input property for carousel configuration
  @Output() mediaReady = new EventEmitter<void>();
  animationDirection: 'left' | 'right' | null = null; // Track the animation direction
  themeType: string = 'light'; // Default theme type
  defaultPosition: string = 'center'; // Default position for slides
  isCarousePaused: boolean = false; // Flag to check if the carousel is paused
  private readonly imageObjectUrlCache = new Map<string, string>();
  private readonly createdObjectUrls: string[] = [];
  private hasEmittedMediaReady = false;

  private stableStringify(value: any): string {
    try {
      return JSON.stringify(value ?? {});
    } catch {
      return '';
    }
  }

  constructor(
    private readonly sanitizer:DomSanitizer,
    private readonly router: Router,
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly sharedService: SharedService
  ) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.hasEmittedMediaReady = false;
    // Build slide view-model in one pass.
    this.slides = (this.carouselConfig.slides || []).map((slide: any) => ({
      ...slide,
      imagePost: slide.imagePost,
      safeHTML: this.sanitizer.bypassSecurityTrustHtml(slide.description || '')
    }));

    // Resolve protected API images to blob: URLs so we don't pass tokens in query params.
    void this.resolveSlideImages();
    this.initialiseCarousel(); // Re-initialize the carousel when the config changes

    if (!this.slides.length) {
      this.emitMediaReady();
    }
  }

  initialiseCarousel() {
    this.slides = this.sortSlidesByOrder(); // Sort slides by occurence property
    this.themeType = this.slides[0]?.theme || 'light'; // Set theme type based on the first slide
    this.defaultPosition = this.slides[0]?.position || 'center'; // Set default position based on the first slide
    if (this.carouselConfig.autoplay) {
      this.startAutoplay(); // Start autoplay when the component initializes
    }
    const carousel = this.el.nativeElement.querySelector('.carousel-container');
    if (this.carouselConfig.pauseOnHover) {
      this.renderer.listen(carousel, 'mouseenter', () => {
        if(!this.isCarousePaused) // Check if the carousel is not paused
          this.stopAutoplay(); // Stop autoplay on mouse enter
      });
      this.renderer.listen(carousel, 'mouseleave', () => {
        if(!this.isCarousePaused) // Check if the carousel is not paused
          this.startAutoplay(); // Restart autoplay on mouse leave
      });
    }
  }

  sortSlidesByOrder() {
   return  this.slides.sort((a, b) => a.occurence - b.occurence); // Sort slides by occurence property
  }

  ngOnDestroy(): void {
    // Clear the autoplay interval when the component is destroyed
    this.stopAutoplay();

    // Revoke any blob: URLs we created.
    this.createdObjectUrls.forEach(u => {
      try {
        URL.revokeObjectURL(u);
      } catch {
        // ignore
      }
    });
    this.createdObjectUrls.length = 0;
    this.imageObjectUrlCache.clear();
  }

  private stripTokenQueryParam(url: string): string {
    if (!url) return '';
    try {
      const parsed = new URL(url, environment.apiUrl);
      parsed.searchParams.delete('token');
      parsed.searchParams.delete('access_token');
      return parsed.toString();
    } catch {
      return url;
    }
  }

  private normalizeMediaUrl(url: string): string {
    if (!url) return '';
    const trimmed = url.trim();
    if (trimmed.length === 0) return '';
    if (/^(blob:|data:)/i.test(trimmed)) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return this.stripTokenQueryParam(trimmed);
    return this.stripTokenQueryParam(
      `${environment.apiUrl}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`
    );
  }

  private isApiOrigin(url: string): boolean {
    try {
      const base = new URL(environment.apiUrl);
      const resolved = new URL(url, environment.apiUrl);
      return resolved.origin === base.origin;
    } catch {
      return false;
    }
  }

  private async resolveSlideImages(): Promise<void> {
    const slides = this.slides || [];
    await Promise.all(
      slides.map(async (slide) => {
        const imagePost = (slide as any)?.imagePost;
        if (imagePost && typeof imagePost === 'object') {
          const method = String(imagePost?.method || '').toUpperCase();
          const url = String(imagePost?.url || '').trim();
          if (method === 'POST' && url) {
            await this.resolveSlideImageFromPost(slide, { url, body: imagePost?.body });
            return;
          }
        }

        const raw = String(slide?.image || '').trim();
        if (!raw) return;

        const normalized = this.normalizeMediaUrl(raw);
        if (!normalized || /^(blob:|data:)/i.test(normalized)) {
          slide.image = normalized;
          return;
        }

        // Fallback: if backend did not provide imagePost, use direct URL.
        // Cookies are scoped to /v4/sync/blob so the browser will attach them for media.
        slide.image = normalized;
      })
    );
  }

  private async resolveSlideImageFromPost(
    slide: slideDetails,
    config: { url: string; body?: any }
  ): Promise<void> {
    const normalizedUrl = this.normalizeMediaUrl(config.url);
    if (!normalizedUrl) return;

    const cacheKey = `POST ${normalizedUrl} ${this.stableStringify(config.body)}`;
    const cached = this.imageObjectUrlCache.get(cacheKey);
    if (cached) {
      slide.image = cached;
      return;
    }

    try {
      const resolved = await this.sharedService.postImageAsRenderableUrl(normalizedUrl, config.body ?? {}, {
        withCredentials: false,
        includeAuthorization: false
      });
      if (!resolved?.url) {
        slide.error = true;
        return;
      }

      if (resolved.needsRevoke && resolved.url.startsWith('blob:')) {
        this.createdObjectUrls.push(resolved.url);
      }
      this.imageObjectUrlCache.set(cacheKey, resolved.url);
      slide.image = resolved.url;
      slide.error = false;
    } catch {
      // If POST fails, leave slide.image as-is; template error handler can mark it.
    }
  }

  trackBySlide = (_: number, slide: slideDetails): any => {
    return slide?.id ?? slide?.occurence;
  };

  nextSlide(): void {
    this.animationDirection = 'right'; // Set animation direction to right for next slide
    if (this.currentSlide === this.slides.length - 1)
      this.currentSlide = 0; // Reset to the first slide if at the end
    else
      this.currentSlide = (this.currentSlide + 1) % this.slides.length; // Move to the next slide 
    this.updateTheme();
  }

  prevSlide(): void {
    this.animationDirection = 'left'; // Set animation direction to left for previous slide
    if (this.currentSlide === 0) {
      this.currentSlide = this.slides.length - 1; // Reset to the last slide if at the beginning
    } else {
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length; // Move to the previous slide
    }
    this.updateTheme();
  }

  goToSlide(index: number): void {
    this.currentSlide = index; // Go to a specific slide
    this.updateTheme();
  }

  startAutoplay(): void {
    this.autoplayInterval = setInterval(() => {
      this.nextSlide(); // Automatically move to the next slide every 3 seconds
    }, this.carouselConfig.interval || 3000); // Default interval is 3 seconds
  }

  stopAutoplay(): void {
    clearInterval(this.autoplayInterval); // Stop the autoplay interval
  }

  onButtonClick(buttonData: any){
    const url = buttonData.split(';')[1]; // Extract the URL from the button data
    if(url) {
      window.open(url, '_blank'); // Open the URL in a new tab
    }
  }

  updateTheme(){
    this.themeType = this.slides[this.currentSlide]?.theme || 'light'; // Update theme type based on the current slide
    this.defaultPosition = this.slides[this.currentSlide]?.position || 'center'; // Update default position based on the current slide
  }

  onPlayPauseButton(){
    this.isCarousePaused = !this.isCarousePaused; // Toggle the pause state
    if (this.isCarousePaused) {
      this.stopAutoplay(); // Stop autoplay when paused
    }else {
      this.startAutoplay(); // Start autoplay when resumed
    }
  }

  onImageError(index: number): void {
    // Handle image loading errors by setting a background
    this.slides[index].error = true; // Mark the slide as having an error
    if (index === this.currentSlide) {
      this.emitMediaReady();
    }
  }

  onImageLoad(index: number): void {
    if (index === this.currentSlide) {
      this.emitMediaReady();
    }
  }

  private emitMediaReady(): void {
    if (this.hasEmittedMediaReady) {
      return;
    }

    this.hasEmittedMediaReady = true;
    this.mediaReady.emit();
  }

}
